import VendorDeliveryInfo from '@/dealers/pages/Order/Details/VendorDeliveryInfo'
import Address from '@/gf/components/Address'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Field from '@/gf/components/Field'
import ItemQuantitiesInput from '@/gf/components/ItemQuantitiesInput'
import Layout from '@/gf/components/Layout'
import TextField from '@/gf/components/TextField'
import DateTimeInput from '@/gf/components/inputs/DateTime'
import ShippingCarrierInput from '@/gf/components/next/ShippingCarrierInput'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import StoreOrderM from '@/gf/modules/StoreOrder'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { ShipmentItemInput, ShippingPayload } from '../../types'
import { PaymentMethod } from '../_gen/gql'
import { StoreOrdersSearch } from '../modules/api'
import AddressMetadata from './AddressMetadata'
import EmailActionModal from './EmailActionModal'

type ShippingTab = 'Shipping Carrier' | 'Delivery'

type VendorDeliveryPayload = {
  outForDeliveryAt: DateTime
  note: string | null
}

const ShippingForm = ({
  storeOrder,
  spinnerLive,
  onSubmit,
  onSubmitVendorDelivery,
}: {
  storeOrder: StoreOrdersSearch['storeOrders'][number]
  spinnerLive: boolean
  onSubmit: (shippingPayload: ShippingPayload) => Promise<void>
  onSubmitVendorDelivery: (payload: VendorDeliveryPayload) => Promise<void>
}) => {
  const [_msgs, msgsMgr] = useMsgs()
  const [trackingNumber, setTrackingNumber] = useState<string>()
  const [shippingCarrier, setShippingCarrier] = useState<string | null>(null)
  const [shippingModalOpen, shippingModalToggler] = useToggle()
  const [shippingEmailAdditionalCommentsText, setShippingEmailAdditionalCommentsText] = useState('')
  const [shippingEmailCheckbox, setShippingEmailCheckbox] = useState<boolean>(true)
  const [selectedTabName, setSelectedTabName] = useState<ShippingTab>('Shipping Carrier')
  const [outForDeliveryAt, setOutForDeliveryAt] = useState(DateTime.now())

  const unshippedLineItems = StoreOrderM.getUnshipped(storeOrder.lineItems)
  const [lineItemQuantities, setLineItemQuantities] = useState<Record<string, string>>(
    unshippedLineItems.reduce((acc, lineItem) => ({ ...acc, [lineItem.id]: lineItem.quantity }), {})
  )
  const shipmentItemsCount = Object.values(lineItemQuantities).reduce(
    (acc, quantity) => acc + (parseFloat(quantity) || 0),
    0
  )
  const possibleShipmentItemsCount = unshippedLineItems.reduce(
    (acc, lineItem) => acc + lineItem.quantity,
    0
  )

  const handleSubmit = () => {
    if (selectedTabName === 'Shipping Carrier') {
      // Create Shipment
      const shipmentItems: ShipmentItemInput[] = Object.entries(lineItemQuantities)
        .map(([lineItemId, quantity]) => ({
          lineItemId,
          quantity: parseFloat(quantity) || 0,
        }))
        .filter((shipmentInput) => shipmentInput.quantity !== 0)
      onSubmit({
        storeOrderId: storeOrder.id,
        shipmentItems,
        trackingNumber,
        shippingCarrier: shippingCarrier ?? '',
        trackingLink: undefined,
        sendEmail: shippingEmailCheckbox,
        notes: shippingEmailAdditionalCommentsText,
      })
    } else {
      // Create Delivery
      onSubmitVendorDelivery({ outForDeliveryAt, note: shippingEmailAdditionalCommentsText })
    }
  }

  useEffect(() => {
    if (!spinnerLive) {
      shippingModalToggler.off()
    }
  }, [spinnerLive])

  // Don't allow delivery if items have already been shipped
  // Only allow delivery for direct pay orders
  const canDeliver =
    storeOrder.paymentMethod === PaymentMethod.Direct &&
    storeOrder.lineItems.reduce(
      (hasShipment, lineItem) => hasShipment && lineItem.shipmentItems.length === 0,
      true
    )

  return (
    <>
      <EmailActionModal
        title={selectedTabName === 'Shipping Carrier' ? 'Ship Order' : 'Deliver Order'}
        open={shippingModalOpen}
        onClose={shippingModalToggler.off}
        onSubmit={handleSubmit}
        isWaiting={spinnerLive}
        emailText={shippingEmailAdditionalCommentsText}
        setEmailText={setShippingEmailAdditionalCommentsText}
        sendEmailCheckbox={shippingEmailCheckbox}
        setSendEmailCheckbox={setShippingEmailCheckbox}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (selectedTabName === 'Shipping Carrier') {
            // Validate shipping info
            if (shipmentItemsCount === 0) {
              msgsMgr.add('Cannot create a shipment with no items.', 'negative')
              return
            }
            if (!(trackingNumber && shippingCarrier)) {
              msgsMgr.add('Add tracking information to create shipment', 'negative')
              return
            }
          }

          shippingModalToggler.on()
        }}
      >
        <Layout>
          <Layout.Section type="primary">
            <Card
              {...(canDeliver
                ? {
                    tabs: [{ name: 'Shipping Carrier' }, { name: 'Delivery' }],
                    currentTabName: selectedTabName,
                    onTabSelect: (tabName) => setSelectedTabName(tabName as ShippingTab),
                  }
                : {})}
            >
              {selectedTabName === 'Shipping Carrier' ? (
                <>
                  <ItemQuantitiesInput
                    items={unshippedLineItems}
                    itemQuantities={lineItemQuantities}
                    onChangeItemQuantities={setLineItemQuantities}
                  />
                  <Card.Section title="Tracking Information">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Shipping Carrier">
                        <ShippingCarrierInput
                          value={shippingCarrier}
                          onChange={(carrier) => setShippingCarrier(carrier)}
                        />
                      </Field>
                      <TextField
                        label="Tracking Number"
                        placeholder="i.e. 134509253094, 1ZP92387"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                      />
                    </div>
                  </Card.Section>
                </>
              ) : (
                <>
                  <Card.Section innerClassName="flex flex-col gap-y-6 max-w-prose">
                    <VendorDeliveryInfo />
                    <div>
                      <p className="pb-2 font-medium">When did this order go out for delivery?</p>
                      <div className="flex gap-x-2 items-center">
                        <DateTimeInput
                          dateTime={outForDeliveryAt}
                          updateDateTime={(ra) => ra && setOutForDeliveryAt(ra)}
                          required
                        />
                        <p className="text-sm text-slate-600 italic">(Approximate day & time)</p>
                      </div>
                    </div>
                  </Card.Section>
                </>
              )}
            </Card>
          </Layout.Section>
          <Layout.Section type="secondary">
            <Card title="Shipping Address">
              <Card.Section>
                {storeOrder.shippingAddress ? (
                  <>
                    <Address
                      className="text-sm text-gray-700"
                      address={storeOrder.shippingAddress}
                    />
                    {typeof storeOrder.shippingAddress.deliverable === 'boolean' && (
                      <AddressMetadata className="mt-2" address={storeOrder.shippingAddress} />
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-700">Shipping address not specified</p>
                )}
              </Card.Section>
            </Card>
            <Card title="Summary">
              <Card.Section>
                <p className="text-sm text-gray-700">
                  Creating shipment containing {shipmentItemsCount} of {possibleShipmentItemsCount}{' '}
                  items.
                </p>
              </Card.Section>
              <Card.Section>
                <Button
                  type="submit"
                  title="Submit"
                  className="w-full justify-center"
                  performing={spinnerLive}
                  primary
                />
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </form>
    </>
  )
}

export default ShippingForm
